import { Fragment, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { usePagedResource } from '../lib/usePagedResource';
import { recentYears } from '../lib/format';
import { EmptyState, ErrorNotice, SearchField, Spinner, YearField } from './ui';

export interface Column<T> {
  header: string;
  align?: 'left' | 'right';
  render: (row: T) => ReactNode;
}

export interface ListPageProps<T> {
  title: string;
  desc?: string;
  endpoint: string;
  columns: Column<T>[];
  // 当页展开详情（不使用右侧抽屉）
  renderDetail: (row: T) => ReactNode;
  rowId: (row: T) => string;
  pageSize?: number;
  searchable?: boolean;
  showYear?: boolean;
  searchPlaceholder?: string;
  // [PATCH-5] 附加筛选渲染位：以 render prop 下发列表资源，父组件才能绑定
  //   item_id / active 等到 res.setItemId / res.setActive（如材料方案的适用物料、启用状态）
  filters?: (res: ReturnType<typeof usePagedResource<T>>) => React.ReactNode;
  actions?: ReactNode;
  // 行级操作列（编辑/删除等）。提供后会在表尾追加「操作」列，点击其中的按钮不会触发行展开。
  rowActions?: (row: T) => ReactNode;
  // 全局搜索跳转时带入的关键字（例如 /contracts?q=CON-…）；变化时同步到列表搜索框。
  initialQuery?: string;
  // 父组件 bump 该数值可强制列表重新拉取（例如当页详情保存后）。
  reloadSignal?: number;
  // 初始展开的行（例如从质检审核队列点「去审核」带 ?open=<id> 跳进来）
  initialExpandedId?: string | null;
}

export function ListPage<T>({
  title, desc, endpoint, columns, renderDetail, rowId,
  pageSize = 50, searchable = true, showYear = true, searchPlaceholder, actions, rowActions, reloadSignal, initialQuery, initialExpandedId, filters,
}: ListPageProps<T>) {
  const res = usePagedResource<T>(endpoint, pageSize);
  const [expanded, setExpanded] = useState<string | null>(initialExpandedId ?? null);
  // 队列里点不同的 BOM 时 prop 会变，展开目标要跟着走
  useEffect(() => {
    if (initialExpandedId) setExpanded(initialExpandedId);
  }, [initialExpandedId]);
  const years = recentYears();

  useEffect(() => {
    if (reloadSignal !== undefined) res.reload();
    // 仅在父组件 bump reloadSignal 时触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadSignal]);

  const lastInitial = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (initialQuery !== undefined && initialQuery !== lastInitial.current) {
      lastInitial.current = initialQuery;
      res.setQ(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  const toggle = (id: string) => setExpanded((cur) => (cur === id ? null : id));
  const colCount = columns.length + (rowActions ? 1 : 0);

  return (
    <section className="panel">
      <div className="panel-head">
        <div style={{ minWidth: 0 }}>
          <h2>{title}</h2>
          {desc ? <div className="desc">{desc}</div> : null}
        </div>
        <div className="spacer" />
        {actions}
      </div>

      <div className="toolbar" style={{ alignItems: 'flex-end' }}>
        {searchable ? (
          <div className="field" style={{ flex: 1, minWidth: 240 }}>
            <label>关键字搜索</label>
            <SearchField value={res.q} onChange={res.setQ} placeholder={searchPlaceholder} />
          </div>
        ) : null}
        {showYear ? <YearField value={res.year} onChange={res.setYear} years={years} /> : null}
        {filters ? filters(res) : null}
        <div className="field">
          <label>操作</label>
          <button className="btn" onClick={res.reload} disabled={res.loading} title="重新拉取">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 12a9 9 0 1 1-3-6.7" strokeLinecap="round" /><path d="M21 4v4h-4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            刷新
          </button>
        </div>
      </div>

      {res.error ? (
        <div style={{ padding: 'var(--pad)' }}>
          <ErrorNotice message={res.error} onRetry={res.reload} />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.header} style={{ textAlign: c.align || 'left' }}>{c.header}</th>
                ))}
                {rowActions ? <th className="op-col" style={{ textAlign: 'right' }}>操作</th> : null}
              </tr>
            </thead>
            <tbody>
              {res.loading && res.rows.length === 0 ? (
                <tr>
                  <td colSpan={colCount} style={{ padding: 'var(--pad)' }}>
                    <Spinner label="正在从核心服务读取数据…" />
                  </td>
                </tr>
              ) : res.rows.length === 0 ? (
                <tr>
                  <td colSpan={colCount} style={{ padding: 0 }}>
                    <EmptyState>没有符合条件的记录。试试清空搜索或切换年度。</EmptyState>
                  </td>
                </tr>
              ) : (
                res.rows.map((row) => {
                  const id = rowId(row);
                  const open = expanded === id;
                  return (
                    <Fragment key={id}>
                      <tr
                        className={open ? 'sel-row' : undefined}
                        onClick={() => toggle(id)}
                        aria-expanded={open}
                      >
                        {columns.map((c) => (
                          <td key={c.header} style={{ textAlign: c.align || 'left' }}>{c.render(row)}</td>
                        ))}
                        {rowActions ? (
                          <td className="op-col" style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                            {rowActions(row)}
                          </td>
                        ) : null}
                      </tr>
                      {open ? (
                        <tr className="detail-row">
                          {/* 用 colCount 而不是 columns.length：提供了 rowActions 时表尾多一列「操作」，
                              少跨一列会让详情块右侧空出一截（新增 ARCA 开票列表的 PDF 操作列后暴露）。 */}
                          <td colSpan={colCount}>
                            {renderDetail(row)}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="pager">
        <span className="info">{res.loading ? '加载中…' : res.rangeLabel}</span>
        <div className="spacer" />
        <button className="btn" onClick={res.prev} disabled={!res.canPrev || res.loading}>上一页</button>
        <button className="btn" onClick={res.next} disabled={!res.canNext || res.loading}>下一页</button>
      </div>
    </section>
  );
}
